/**
 * The offerings piece's standard look: one row per offering, the name over
 * "Sat, Nov 2, 6:00 PM · 120 min · 2 spots left" (a service: "Next open Thu,
 * Oct 30 · 90 min"), the price at the right. Draws the behavior half's state
 * and calls its actions; holds no rule. Draws nothing with no offering.
 */
import { useId } from 'react';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { onLink } from '../../ui/onLink';
import { contract } from './contract';
import css from './Offerings.module.css';
import type { OfferingsRuntime } from './useOfferings';

const part = partsOf(contract);

export function OfferingsView({ state, actions, options }: OfferingsRuntime) {
  // Placed on a custom page (web-studio-sites D4): no band and no frame.
  const placed = usePlaced();
  const headingId = useId();
  if (state.offerings.length === 0) return null;
  return (
    <section
      className={placed ? undefined : shared.sec}
      aria-labelledby={options.heading ? headingId : undefined}
      {...pieceRoot(contract)}
      {...part('section')}
    >
      <div className={placed ? undefined : shared.wrap}>
        {options.heading && (
          <h2 className={`${shared.h2} ${css.heading}`} id={headingId} {...part('heading')}>
            {options.heading}
          </h2>
        )}
        <ul className={css.list} {...part('list')}>
          {state.offerings.map((o) => (
            <li key={`${o.kind}:${o.id}`}>
              <a className={css.row} href={o.path} onClick={onLink(() => actions.open(o.id))} {...part('item')}>
                <h3 className={`${shared.h3} ${css.name}`} {...part('name')}>
                  {o.name}
                </h3>
                <span className={css.line}>
                  <span {...part('when')}>{o.kind === 'event' ? `${o.day}, ${o.time}` : `Next open ${o.day}`}</span>
                  <span className={css.sep} aria-hidden="true" {...part('separator')}>
                    ·
                  </span>
                  <span {...part('length')}>{o.length}</span>
                  {o.spots && (
                    <>
                      <span className={css.sep} aria-hidden="true" {...part('separator')}>
                        ·
                      </span>
                      <span className={css.low} {...part('spots', 'low')}>
                        {o.spots}
                      </span>
                    </>
                  )}
                </span>
                <b className={`${css.price} ${o.price.kind === 'FREE' ? css.free : ''}`} {...part('price')}>
                  {o.priceLabel}
                </b>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
